import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { hotDeals, heroSlides, stats, faqs } from '@/mocks/home';
import { categories, allArticles } from '@/mocks/categories';

function HeroSection() {
  const slide = heroSlides[0];
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(true); }, []);

  return (
    <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={slide.image}
          alt="福利吧每日精选福利"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
      </div>

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
        <div className={`text-center max-w-3xl transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/20 text-primary-300 text-sm font-medium mb-6 border border-primary-400/30 backdrop-blur-sm">
            {slide.subtitle}
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-background-50 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {slide.title}
          </h1>
          <p className="text-lg md:text-xl text-background-200/80 mb-8 max-w-xl mx-auto">
            {slide.description}
          </p>
          <Link
            to={slide.link}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-semibold text-base transition-all duration-200 no-underline whitespace-nowrap cursor-pointer"
          >
            {slide.cta}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3">
        <div className="w-8 h-1 rounded-full bg-primary-500"></div>
        <div className="w-2 h-1 rounded-full bg-background-300/50"></div>
        <div className="w-2 h-1 rounded-full bg-background-300/50"></div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="w-full bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat, index) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-all duration-200 no-underline cursor-pointer"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                cat.color === 'primary'
                  ? 'bg-primary-100 text-primary-600'
                  : cat.color === 'accent'
                  ? 'bg-accent-100 text-accent-600'
                  : 'bg-secondary-100 text-secondary-600'
              }`}>
                <i className={`${cat.icon} text-lg`}></i>
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground-900 block whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="text-xs text-foreground-500">{cat.description.slice(0, 8)}...</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="w-full bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-4">
              <div className="text-2xl md:text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>
                {stat.value}
              </div>
              <div className="text-sm text-foreground-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedDeals() {
  return (
    <section className="w-full bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            热门福利推荐
          </h2>
          <p className="mt-3 text-foreground-600 text-sm md:text-base">
            精选本周最受欢迎的福利内容，不要错过任何一个省钱机会
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {hotDeals.map((deal, index) => (
            <Link
              key={deal.slug}
              to={`/article/${deal.slug}`}
              className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 no-underline cursor-pointer"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative overflow-hidden h-48">
                <img
                  src={deal.image}
                  alt={deal.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                  deal.tagColor === 'primary'
                    ? 'bg-primary-500 text-background-50'
                    : deal.tagColor === 'accent'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-secondary-500 text-background-50'
                }`}>
                  {deal.tag}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-950 text-base group-hover:text-primary-600 transition-colors">
                  {deal.title}
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">{deal.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/category/limited-coupons"
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 no-underline whitespace-nowrap cursor-pointer"
          >
            查看更多福利
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function LatestOffers() {
  return (
    <section className="w-full bg-background-100/70">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              最新福利动态
            </h2>
            <p className="mt-1.5 text-foreground-600 text-sm">每日更新，福利不停歇</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-3 md:space-y-4">
          {allArticles.slice(0, 8).map((article, index) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="flex flex-col sm:flex-row items-start gap-4 p-4 rounded-xl bg-background-50 hover:bg-background-100 transition-all duration-200 no-underline cursor-pointer group"
              style={{ animationDelay: `${index * 0.06}s` }}
            >
              <div className="w-full sm:w-40 h-24 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    article.tagColor === 'primary'
                      ? 'bg-primary-100 text-primary-700'
                      : article.tagColor === 'accent'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {article.tag}
                  </span>
                  <span className="text-xs text-foreground-500">{article.category}</span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h3 className="font-semibold text-foreground-950 text-sm md:text-base group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 mt-1 line-clamp-2">{article.excerpt}</p>
              </div>
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-background-100 group-hover:bg-primary-100 transition-colors flex-shrink-0 mt-2">
                <i className="ri-arrow-right-line text-foreground-500 group-hover:text-primary-600"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <section className="w-full bg-background-50" id="faq">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            常见问题
          </h2>
          <p className="mt-3 text-foreground-600 text-sm">关于福利吧，你想知道的都在这里</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl bg-background-100 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                onClick={() => toggle(index)}
              >
                <span className="font-semibold text-foreground-900 text-sm md:text-base pr-4">
                  {faq.question}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${
                  openIndex === index ? 'bg-primary-100 rotate-45' : 'bg-background-200'
                }`}>
                  <i className={`text-lg ${openIndex === index ? 'text-primary-600' : 'text-foreground-500'} ri-add-line`}></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-4 px-4 md:px-5' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const orgSchema = {
    name: '福利吧',
    url: 'https://fuliba.com',
    description: '福利吧是专注精选优惠、折扣、免费资源与会员权益的福利聚合平台。',
    sameAs: ['https://fuliba.com'],
  };

  const websiteSchema = {
    name: '福利吧',
    url: 'https://fuliba.com',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://fuliba.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Layout>
      <SEOHead
        title="福利吧 - 每日精选福利汇聚，省钱好物一站搞定"
        description="福利吧是专注精选优惠、折扣、免费资源与会员权益的福利聚合平台。每日更新海量好物福利、限时优惠券、积分兑换及专属会员礼遇，帮助用户以最低成本享受最高品质生活。"
        keywords="福利吧,每日福利精选,限时优惠券,免费资源领取,会员专属福利,折扣好物推荐,积分兑换活动"
        canonicalUrl="https://fuliba.com"
      />
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="WebSite" data={websiteSchema} />
      <SchemaOrg type="FAQPage" data={faqSchema} />

      <HeroSection />
      <CategoryNav />
      <StatsSection />
      <FeaturedDeals />
      <LatestOffers />
      <FAQSection />

      <section className="w-full bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50" style={{ fontFamily: 'var(--font-heading)' }}>
            省钱好物一站搞定
          </h2>
          <p className="mt-3 text-background-100/80 text-sm md:text-base">
            加入福利吧，每天发现新的省钱机会。优惠券、免费资源、会员福利——我们帮你一网打尽。
          </p>
          <Link
            to="/category/limited-coupons"
            className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-background-50 text-primary-600 rounded-full font-semibold text-base hover:bg-background-100 transition-all duration-200 no-underline whitespace-nowrap cursor-pointer"
          >
            立即开始探索
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}